import { MethodOptions_IdempotencyLevel } from "@bufbuild/protobuf/wkt";
import { Code, ConnectError, type Interceptor } from "@connectrpc/connect";

/**
 * Bounded, jittered retry for the catalog's side-effect-free reads.
 *
 * The site's one calendar read (`Catalog.ListBroadcasts`) used to fail closed
 * on the first transport error, and a content-service restart is ~31 s of 502s
 * from the tunnel (failures ledger, 2026-09-22 rank 7): every deploy during a
 * round was a visible "calendar unavailable" on the homepage, and the round
 * page's archive reads (`GetTournament`, `GetEdition`) blanked the same way.
 *
 * This is a connect-es interceptor on the catalog transport, so every unary
 * read the site makes gets it without a call site changing. What it retries is
 * decided by the PROTO, not by a list here: a method declared
 * `idempotency_level = NO_SIDE_EFFECTS` (`proto/catalog.proto`) may be re-asked
 * freely; anything else, and any stream, passes straight through.
 *
 * Bounded on three axes — {@link RetryPolicy.maxAttempts}, a total
 * {@link RetryPolicy.deadlineMs} the next sleep must fit inside, and a delay cap
 * — and cancellation-safe: the call's own `AbortSignal` wakes the sleep, and an
 * aborted call never re-asks and surfaces as `CANCELED`, exactly as an aborted
 * first attempt would.
 */
export interface RetryPolicy {
  /** Wall-clock budget for the whole call, first attempt included. A retry that could not fit is not started. */
  deadlineMs: number;
  /** Attempts in total, the first included. */
  maxAttempts: number;
  /** The first backoff; it doubles per retry. */
  baseDelayMs: number;
  /** The longest single backoff. */
  maxDelayMs: number;
  /** ± fraction of a backoff, so a fleet of tabs does not re-ask in lockstep. */
  jitter: number;
}

/**
 * The catalog transport's policy. ~10 s covers the Cloudflare tunnel's 502
 * burst while a content-service instance comes back and a Cloud Run
 * scale-from-zero cold start, without holding a page's first paint hostage:
 * the homepage's own re-fetch loop (`pages/home.ts`) is what covers a longer
 * outage, and it is visible while it does.
 */
export const CATALOG_RETRY: RetryPolicy = { deadlineMs: 10_000, maxAttempts: 5, baseDelayMs: 300, maxDelayMs: 3_000, jitter: 0.25 };

export interface RetryDeps {
  now?: () => number;
  /** Resolves after `ms`, or rejects as soon as `signal` aborts. */
  sleep?: (ms: number, signal: AbortSignal) => Promise<void>;
  /** `[0, 1)`; the jitter's source. */
  random?: () => number;
}

/** The codes a restart, a tunnel or a cold start produce; never one the client caused. */
const RETRYABLE = new Set<Code>([Code.Unavailable, Code.Unknown, Code.Internal, Code.DeadlineExceeded, Code.ResourceExhausted, Code.Aborted]);

/**
 * Is this failure worth asking again about?
 *
 * A `ConnectError` is judged by its code. Anything else the transport throws
 * is `fetch` itself failing — a network error, which is the most transient
 * thing there is — except an abort, which is the caller's decision.
 */
export function isRetryable(err: unknown): boolean {
  if (err instanceof ConnectError) return RETRYABLE.has(err.code);
  if (err instanceof Error && (err.name === "AbortError" || err.name === "TimeoutError")) return false;
  return true;
}

/** The backoff before retry number `attempt` (1-based): base × 2^(attempt−1), capped, ± jitter. */
export function backoffDelayMs(attempt: number, policy: RetryPolicy, random: () => number): number {
  const exponential = Math.min(policy.maxDelayMs, policy.baseDelayMs * 2 ** Math.max(0, attempt - 1));
  const spread = exponential * policy.jitter;
  return Math.round(exponential - spread + 2 * spread * random());
}

/** `setTimeout` that the signal can cut short. */
function sleepUnlessAborted(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(canceled(signal));
      return;
    }
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(canceled(signal));
    };
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

/** What an aborted call surfaces as: the signal's own reason when it is a ConnectError (a deadline), else CANCELED. */
const canceled = (signal: AbortSignal): ConnectError => ConnectError.from(signal.reason ?? "This operation was aborted.", Code.Canceled);

/**
 * A SHARED request, as one caller sees it: `promise`'s answer, unless this caller's `signal`
 * aborts first, in which case THIS caller rejects as {@link canceled} and the request runs on for
 * everyone else sharing it. The listener is removed when `promise` settles.
 *
 * For a memo that hands one in-flight request to several callers (`catalog.ts`'s calendar and
 * tournament reads): starting that request with the first caller's signal let one page's teardown
 * cancel every other page's answer (#3115).
 */
export function raceAbort<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
  if (signal.aborted) return Promise.reject(canceled(signal));
  return new Promise<T>((resolve, reject) => {
    const onAbort = (): void => reject(canceled(signal));
    signal.addEventListener("abort", onAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener("abort", onAbort);
        resolve(value);
      },
      (err: unknown) => {
        signal.removeEventListener("abort", onAbort);
        reject(err);
      },
    );
  });
}

/**
 * The interceptor. Retries a unary, side-effect-free call on a retryable error
 * while attempts, deadline and the caller's signal all allow it.
 */
export function retryInterceptor(policy: RetryPolicy, deps: RetryDeps = {}): Interceptor {
  const now = deps.now ?? Date.now;
  const sleep = deps.sleep ?? sleepUnlessAborted;
  const random = deps.random ?? Math.random;
  return (next) => async (req) => {
    if (req.stream || req.method.idempotency !== MethodOptions_IdempotencyLevel.NO_SIDE_EFFECTS) return next(req);
    const startedAt = now();
    for (let attempt = 1; ; attempt += 1) {
      try {
        return await next(req);
      } catch (err) {
        if (req.signal.aborted) throw canceled(req.signal);
        if (!isRetryable(err) || attempt >= policy.maxAttempts) throw err;
        const delay = backoffDelayMs(attempt, policy, random);
        if (now() - startedAt + delay > policy.deadlineMs) throw err;
        await sleep(delay, req.signal);
        if (req.signal.aborted) throw canceled(req.signal);
      }
    }
  };
}
